import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { toast } from '../../../Toaster/toast';
import { PrismBanner, PrismButton, PrismLoading } from '@prism-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCheck, faCheckCircle, faChevronDown, faChevronUp, faClock, faSort, faTrash } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from '../../../UserAvatar/UserAvatar';
import { stripNameTitle } from '../../../../utils/nameUtils';
import clearLogoSrc from '../../../../assets/images/clear-logo.svg';
import './GraduationPledges.css';
import AdminPane from '../../AdminPane';

const VALID_FREQUENCIES = [
  { value: 'daily', label: 'Daily', hint: 'Sent every day, including weekends.' },
  { value: 'every_2_days', label: 'Every 2 days', hint: 'Sent every other day.' },
  { value: 'every_3_days', label: 'Every 3 days', hint: 'Sent every third day.' },
  { value: 'weekly', label: 'Weekly', hint: 'Sent once a week on Monday.' },
  { value: 'business_days_only', label: 'Business days only', hint: 'Sent Mon–Fri, skipping weekends.' },
];

/** Curated IANA timezone list covering all CLEAR chapter regions */
const TIMEZONE_OPTIONS: { label: string; value: string }[] = [
  // Americas
  { label: 'US — Eastern (New York)',       value: 'America/New_York' },
  { label: 'US — Central (Chicago)',         value: 'America/Chicago' },
  { label: 'US — Mountain (Denver)',         value: 'America/Denver' },
  { label: 'US — Pacific (Los Angeles)',     value: 'America/Los_Angeles' },
  { label: 'US — Alaska (Anchorage)',        value: 'America/Anchorage' },
  { label: 'US — Hawaii (Honolulu)',         value: 'Pacific/Honolulu' },
  { label: 'Canada — Toronto',              value: 'America/Toronto' },
  { label: 'Canada — Vancouver',            value: 'America/Vancouver' },
  { label: 'Mexico — Mexico City',          value: 'America/Mexico_City' },
  { label: 'Brazil — São Paulo',             value: 'America/Sao_Paulo' },
  // Europe
  { label: 'UK — London',                   value: 'Europe/London' },
  { label: 'Ireland — Dublin',              value: 'Europe/Dublin' },
  { label: 'Central Europe (Paris/Berlin)',  value: 'Europe/Paris' },
  { label: 'Eastern Europe (Helsinki)',      value: 'Europe/Helsinki' },
  // Asia / Pacific
  { label: 'India (IST)',                    value: 'Asia/Kolkata' },
  { label: 'Pakistan (PKT)',                 value: 'Asia/Karachi' },
  { label: 'Bangladesh (BST)',               value: 'Asia/Dhaka' },
  { label: 'Singapore / Malaysia',          value: 'Asia/Singapore' },
  { label: 'Hong Kong',                     value: 'Asia/Hong_Kong' },
  { label: 'China (CST)',                    value: 'Asia/Shanghai' },
  { label: 'Japan (JST)',                    value: 'Asia/Tokyo' },
  { label: 'South Korea (KST)',              value: 'Asia/Seoul' },
  { label: 'Philippines (PHT)',              value: 'Asia/Manila' },
  { label: 'Thailand (ICT)',                 value: 'Asia/Bangkok' },
  { label: 'UAE (GST)',                      value: 'Asia/Dubai' },
  // Australia
  { label: 'Australia — Sydney',            value: 'Australia/Sydney' },
  { label: 'Australia — Melbourne',         value: 'Australia/Melbourne' },
  { label: 'Australia — Perth',             value: 'Australia/Perth' },
  // Fallback
  { label: 'UTC',                            value: 'UTC' },
];

type ReminderConfig = {
  id?: number;
  chapter_id: number;
  enabled: boolean;
  frequency: string;
  start_date: string | null;
  end_date: string | null;
  from_address: string | null;
  send_time: string;
  send_timezone: string;
  last_reminder_sent_at?: string | null;
  last_run_at?: string | null;
  last_run_sent_count?: number;
  last_run_failed_count?: number;
  last_run_error_summary?: string | null;
};

const defaultConfig = (chapterId: number): ReminderConfig => ({
  chapter_id: chapterId,
  enabled: false,
  frequency: 'weekly',
  start_date: null,
  end_date: null,
  from_address: null,
  send_time: '09:00',
  send_timezone: 'UTC',
});

function PledgeRemindersTab({ currentUser, activeChapterId }: { currentUser: any; activeChapterId: number | null }) {
  const [config, setConfig] = useState<ReminderConfig | null>(null);
  const [form, setForm] = useState<ReminderConfig | null>(null);
  const [fetching, setFetching] = useState(false);
  const [saveStatus, setSaveStatus] = useState<null | 'saving' | 'saved' | 'error'>(null);
  const [sendNowModal, setSendNowModal] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userChangedRef = useRef(false);

  const isAdmin = currentUser?.Role?.permission_level >= 100;
  const userChapterId = currentUser?.chapter_id;
  const canModify = isAdmin || activeChapterId === userChapterId;

  // Date validation — only enforced when reminder is enabled
  const startDateError: string | null = form?.enabled && !form?.start_date
    ? 'Start date is required when reminders are enabled.'
    : null;
  const endDateError: string | null = form?.enabled && !form?.end_date
    ? 'End date is required when reminders are enabled.'
    : null;
  const dateRangeError: string | null =
    !startDateError && !endDateError && form?.start_date && form?.end_date
      ? (form.start_date >= form.end_date ? 'Start date must be before end date.' : null)
      : null;
  const hasFormError = !!(startDateError || endDateError || dateRangeError);

  const doSave = useCallback(async (currentForm: ReminderConfig, chapterId: number) => {
    userChangedRef.current = false;
    setSaveStatus('saving');
    try {
      const r = await axios.put(`/api/grad_pledge_reminder_config/${chapterId}`, {
        enabled: currentForm.enabled,
        frequency: currentForm.frequency,
        start_date: currentForm.start_date || null,
        end_date: currentForm.end_date || null,
        from_address: currentForm.from_address || null,
        send_time: currentForm.send_time || '09:00',
        send_timezone: currentForm.send_timezone || 'UTC',
      });
      setConfig(r.data);
      // Only sync form from server if user hasn't made new changes during the network call
      if (!userChangedRef.current) {
        setForm({ ...r.data });
      }
      setSaveStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus(null), 2000);
    } catch {
      setSaveStatus('error');
      toast.error('Failed to save reminder config. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (!activeChapterId) return;
    // Cancel any pending auto-save from the previous chapter
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    userChangedRef.current = false;
    setSaveStatus(null);
    setFetching(true);
    axios.get(`/api/grad_pledge_reminder_config/${activeChapterId}`)
      .then(r => {
        setConfig(r.data);
        setForm({ ...r.data });
      })
      .catch(() => {
        const d = defaultConfig(activeChapterId);
        setConfig(d);
        setForm({ ...d });
      })
      .finally(() => setFetching(false));
  }, [activeChapterId]);

  // Socket: refresh config when another tab saves
  useEffect(() => {
    const socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    socket.on('grad-pledge-config:updated', (data: { chapter_id: number }) => {
      if (data.chapter_id === activeChapterId) {
        axios.get(`/api/grad_pledge_reminder_config/${activeChapterId}`)
          .then(r => {
            userChangedRef.current = false;
            setConfig(r.data);
            setForm({ ...r.data });
          })
          .catch(() => {});
      }
    });
    return () => { socket.off('grad-pledge-config:updated'); socket.disconnect(); };
  }, [activeChapterId]);

  // Auto-save: debounce 800ms after any user-driven form change
  useEffect(() => {
    if (!form || !activeChapterId || !canModify || !userChangedRef.current) return;
    if (hasFormError) return; // never persist invalid config
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      doSave(form, activeChapterId);
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [form, activeChapterId, canModify, hasFormError, doSave]);

  /** Mark a user-initiated change and update form state */
  const updateForm = (updater: (f: ReminderConfig) => ReminderConfig) => {
    userChangedRef.current = true;
    setForm(f => f ? updater(f) : f);
  };

  const handleSendNow = async () => {
    setSendNowModal(false);
    try {
      const r = await axios.post(`/api/grad_pledge_reminder_config/${activeChapterId}/send_now`);
      toast.success(`Reminders sent: ${r.data.sent} sent, ${r.data.failed} failed.`);
    } catch {
      toast.error('Failed to trigger reminder send. Please try again.');
    }
  };

  if (!form) {
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
        <PrismLoading as="spinner" container="inline" />
      </div>
    );
  }

  return (
    <div className={`grad-pledges-reminders-tab${fetching ? ' grad-pledges-reminders-tab--fetching' : ''}`}>
      <div className="grad-pledges-reminders-heading-row">
        <h6 className="grad-pledges-reminders-heading">Automated Pledge Reminder Settings</h6>
        <span className={`grad-pledges-reminders-status-badge${form.enabled && !hasFormError ? ' enabled' : ' disabled'}`}>
          {form.enabled && !hasFormError ? '● Active' : '● Inactive'}
        </span>
        {saveStatus === 'saving' && <span className="grad-pledges-reminders-save-status">Saving…</span>}
        {saveStatus === 'saved' && <span className="grad-pledges-reminders-save-status saved">Saved ✓</span>}
      </div>

      <div className="grad-pledges-reminders-form">
        <div className="grad-pledges-reminders-field">
          <label>
            <input
              type="checkbox"
              checked={form.enabled}
              disabled={!canModify}
              onChange={e => {
                const checked = e.target.checked;
                updateForm(f => ({ ...f, enabled: checked }));
              }}
            />
            {' '}Enabled
          </label>
          <p className="grad-pledges-reminders-hint">
            When enabled, reminders are sent automatically on a schedule. Once a participant completes their pledge,
            they are automatically removed from future reminder emails — no manual action needed.
          </p>
        </div>

        <div className={`grad-pledges-reminders-fields-body${form.enabled ? '' : ' disabled'}`}>

        <div className="grad-pledges-reminders-field">
          <label htmlFor="reminder-frequency">Frequency</label>
          <select
            id="reminder-frequency"
            className="form-control form-control-sm"
            style={{ maxWidth: 220 }}
            value={form.frequency}
            disabled={!canModify || !form.enabled}
            onChange={e => {
              const value = e.target.value;
              updateForm(f => ({ ...f, frequency: value }));
            }}
          >
            {VALID_FREQUENCIES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {(() => {
            const selected = VALID_FREQUENCIES.find(f => f.value === form.frequency);
            return selected ? <p className="grad-pledges-reminders-hint">{selected.hint}</p> : null;
          })()}
        </div>

        <div className="grad-pledges-reminders-field">
          <label htmlFor="reminder-send-time">Send Time</label>
          <input
            id="reminder-send-time"
            type="time"
            className="form-control form-control-sm"
            style={{ maxWidth: 140 }}
            value={form.send_time || '09:00'}
            disabled={!canModify || !form.enabled}
            onChange={e => {
              const value = e.target.value;
              updateForm(f => ({ ...f, send_time: value }));
            }}
          />
          <p className="grad-pledges-reminders-hint">
            Time of day to send reminders (in the selected timezone below).
          </p>
        </div>

        <div className="grad-pledges-reminders-field">
          <label htmlFor="reminder-timezone">Timezone</label>
          <select
            id="reminder-timezone"
            className="form-control form-control-sm"
            style={{ maxWidth: 320 }}
            value={form.send_timezone || 'UTC'}
            disabled={!canModify || !form.enabled}
            onChange={e => {
              const value = e.target.value;
              updateForm(f => ({ ...f, send_timezone: value }));
            }}
          >
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          {(() => {
            const tz = form.send_timezone || 'UTC';
            const time = form.send_time || '09:00';
            try {
              const [h, m] = time.split(':').map(Number);
              const now = new Date();
              now.setHours(h, m, 0, 0);
              const local = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: tz, timeZoneName: 'short' });
              return <p className="grad-pledges-reminders-hint">Reminders will go out at <strong>{local}</strong>.</p>;
            } catch {
              return null;
            }
          })()}
        </div>

        <div className="grad-pledges-reminders-field">
          <label htmlFor="reminder-start">Start Date</label>
          <input
            id="reminder-start"
            type="date"
            className={`form-control form-control-sm${startDateError || dateRangeError ? ' is-invalid' : ''}`}
            style={{ maxWidth: 180 }}
            value={form.start_date || ''}
            disabled={!canModify || !form.enabled}
            onChange={e => {
              const value = e.target.value;
              updateForm(f => ({ ...f, start_date: value || null }));
            }}
          />
          {startDateError
            ? <p className="grad-pledges-reminders-hint grad-pledges-reminders-date-error">{startDateError}</p>
            : dateRangeError
              ? <p className="grad-pledges-reminders-hint grad-pledges-reminders-date-error">{dateRangeError}</p>
              : <p className="grad-pledges-reminders-hint">First day reminders will be sent.</p>
          }
        </div>

        <div className="grad-pledges-reminders-field">
          <label htmlFor="reminder-end">End Date</label>
          <input
            id="reminder-end"
            type="date"
            className={`form-control form-control-sm${endDateError || dateRangeError ? ' is-invalid' : ''}`}
            style={{ maxWidth: 180 }}
            value={form.end_date || ''}
            disabled={!canModify || !form.enabled}
            onChange={e => {
              const value = e.target.value;
              updateForm(f => ({ ...f, end_date: value || null }));
            }}
          />
          {endDateError
            ? <p className="grad-pledges-reminders-hint grad-pledges-reminders-date-error">{endDateError}</p>
            : <p className="grad-pledges-reminders-hint">Last day reminders will be sent.</p>
          }
        </div>

        {canModify && (
          <div className="grad-pledges-reminders-actions">
            <PrismButton
              display="outline"
              onPrismClick={() => setSendNowModal(true)}
              disabled={!form.enabled || saveStatus === 'saving'}
            >
              Send Reminder Now
            </PrismButton>
          </div>
        )}

        </div>{/* end .grad-pledges-reminders-fields-body */}
      </div>

      {config?.last_run_at && (
        <div className="grad-pledges-reminders-last-run">
          <strong>Last run:</strong> {new Date(config.last_run_at).toLocaleString()}
          {' — '}<strong>Sent:</strong> {config.last_run_sent_count}
          {' — '}<strong>Failed:</strong> {config.last_run_failed_count}
          {config.last_run_error_summary && (
            <div className="clr:text-danger-base" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
              {config.last_run_error_summary}
            </div>
          )}
        </div>
      )}

      {sendNowModal && (
        <ConfirmModal
          title="Send Reminders Now"
          onConfirm={handleSendNow}
          onCancel={() => setSendNowModal(false)}
          confirmLabel="Send Now"
        >
          <p>Immediately send pledge reminders to all unsigned parties for accepted applications in this chapter?</p>
        </ConfirmModal>
      )}
    </div>
  );
}

const SIGNER_ROLES = ['mentee', 'mentor', 'committee', 'manager'];
const SIGNER_ROLE_LABELS: Record<string, string> = {
  mentee: 'Mentee',
  mentor: 'Mentor',
  committee: 'Committee',
  manager: 'Mentee Manager',
};
/** Non-mentee roles rendered as pledge cells in the table */
const PLEDGE_ROLES = ['mentor', 'committee', 'manager'];

type SortColumn = 'mentee' | 'mentor' | 'committee' | 'manager';

/** Return the signed GradPledge record for the given role, or null */
function getPledgeFor(application: any, role: string) {
  return application.GradPledges?.find((p: any) => p.signer_role === role) || null;
}

/** Return the display-name sort key for a given column (empty string when unassigned) */
function getSortKey(app: any, col: SortColumn): string {
  switch (col) {
    case 'mentee':
      return app.User?.name || '';
    case 'mentor':
      return app.assignedMentor?.name || getPledgeFor(app, 'mentor')?.User?.name || '';
    case 'committee':
      return app.assignedCommittee?.name || getPledgeFor(app, 'committee')?.User?.name || '';
    case 'manager':
      return app.assignedManagerUser?.name || (app.manager || '').replace(/@.*$/, '').replace(/_/g, ' ');
  }
}

/** All four roles signed? */
function isFullySigned(application: any) {
  return SIGNER_ROLES.every(role => getPledgeFor(application, role));
}

/** True if at least one role has an assigned person who hasn't signed yet.
 *  Purely-unassigned vacancies don't count — there's nobody to remind. */
function hasReminderTarget(application: any) {
  if (isFullySigned(application)) return false;
  for (const role of SIGNER_ROLES) {
    if (getPledgeFor(application, role)) continue; // already signed
    if (role === 'mentee' && application.User) return true;
    if (role === 'mentor' && application.assignedMentor) return true;
    if (role === 'committee' && application.assignedCommittee) return true;
    if (role === 'manager' && application.manager) return true;
  }
  return false;
}

/** Progress counts across all applications */
function computeProgress(applications: any[]) {
  const total = applications.length;
  const completed = applications.filter(isFullySigned).length;
  const roleCounts: Record<string, { signed: number; total: number }> = {};
  let totalSigned = 0;
  SIGNER_ROLES.forEach(role => {
    const signed = applications.filter(a => getPledgeFor(a, role)).length;
    totalSigned += signed;
    roleCounts[role] = { signed, total };
  });
  const totalPossible = total * SIGNER_ROLES.length;
  return { total, completed, roleCounts, totalSigned, totalPossible };
}

function PledgeCell({ pledge, assignedUser }: { pledge: any; assignedUser?: { id?: number; name: string; photo?: string } | null }) {
  if (pledge) {
    // For organic signatures, pledge.User IS the role holder (correctly capitalized from DB).
    // For admin overrides, signed_by is the admin — use assignedUser to show the role holder instead.
    const isOverride = assignedUser?.id != null && assignedUser.id !== pledge.signed_by;
    const displayUser = (isOverride ? assignedUser : null) || pledge?.User || assignedUser;
    const date = pledge.created_at ? new Date(pledge.created_at).toLocaleDateString() : '';
    return (
      <div
        className="grad-pledges-signer-cell"
        title={date ? `Signed on ${date}` : undefined}
      >
        <UserAvatar user={displayUser} size={24} />
        <span>{stripNameTitle(displayUser?.name)}</span>
      </div>
    );
  }
  if (assignedUser) {
    return (
      <div className="grad-pledges-signer-cell" title="Not yet signed">
        <UserAvatar user={assignedUser} size={24} />
        <span>{stripNameTitle(assignedUser.name)}</span>
      </div>
    );
  }
  return <span className="grad-pledges-signer-cell unsigned">Unassigned</span>;
}

function OverallChip({ application }: { application: any }) {
  if (isFullySigned(application)) {
    return <span className="grad-pledges-chip complete">✓ Complete</span>;
  }
  return <span className="grad-pledges-chip incomplete">Incomplete</span>;
}

function ConfirmModal({
  title, children, onConfirm, onCancel, confirmLabel = 'Confirm', confirmVariant = 'primary', confirmDisabled = false,
}: {
  title: string;
  children: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  confirmDisabled?: boolean;
}) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        className="grad-pledges-confirm-modal"
        style={{ borderRadius: 6, padding: '1.5rem', maxWidth: 480, width: '90%' }}
        onClick={e => e.stopPropagation()}
      >
        <h5 style={{ marginBottom: '1rem' }}>{title}</h5>
        <div style={{ marginBottom: '1.25rem' }}>{children}</div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <PrismButton display="outline" onPrismClick={onCancel}>Cancel</PrismButton>
          {confirmVariant === 'danger'
            ? <PrismButton tone="danger" onPrismClick={onConfirm} disabled={confirmDisabled}>{confirmLabel}</PrismButton>
            : <PrismButton onPrismClick={onConfirm} disabled={confirmDisabled}>{confirmLabel}</PrismButton>
          }
        </div>
      </div>
    </div>
  );
}

const ALL_CHAPTERS_ID = 0; // sentinel for "All chapters" view

const GraduationPledges = ({ currentUser }: { currentUser: any }) => {
  const [chapters, setChapters] = useState<any[]>([]);
  const [activeChapterId, setActiveChapterId] = useState<number | null>(null);
  const [allApplications, setAllApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Section tabs
  const [sectionTab, setSectionTab] = useState<'status' | 'reminders'>('status');

  // Filters / view
  const [filter, setFilter] = useState('all'); // 'all' | 'complete' | 'incomplete'
  const [nameSearch, setNameSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<any>>(new Set());

  // Modals
  const [overrideModal, setOverrideModal] = useState<{ application: any; role: string } | null>(null);
  const [revokeModal, setRevokeModal] = useState<{ pledge: any; menteeName: string } | null>(null);
  const [reminderModal, setReminderModal] = useState<{ applicationIds: number[] } | null>(null);
  const [reminderShowDetails, setReminderShowDetails] = useState(false);
  const [overrideNote, setOverrideNote] = useState('');

  // Column sort
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Row expand / drawer
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [rowTimelines, setRowTimelines] = useState<Record<string, any[] | 'loading' | 'error'>>({});
  const [expandedTileKey, setExpandedTileKey] = useState<string | null>(null);

  const socketRef = useRef<ReturnType<typeof io> | null>(null);

  const isAdmin = currentUser?.Role?.permission_level >= 100;
  const userChapterId = currentUser?.chapter_id;

  // Load chapters + all applications once on mount
  const fetchAllApplications = useCallback((loadedChapters?: any[]) => {
    const chapterSource = loadedChapters
      ? Promise.resolve(loadedChapters)
      : axios.get('/api/chapters').then(r => r.data);

    setLoading(true);
    chapterSource.then(chaps =>
      Promise.all(chaps.map((c: any) =>
        axios.get('/api/grad_pledges/admin', { params: { chapter_id: c.id } })
          .then(r => r.data)
          .catch(() => [])
      ))
    ).then(results => {
      setAllApplications(results.flat());
    }).catch(() => toast.error('Failed to load graduation pledge data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    axios.get('/api/chapters').then(r => {
      setChapters(r.data);
      const defaultChapter = r.data.find((c: any) => c.id === userChapterId) || r.data[0];
      if (defaultChapter) setActiveChapterId(defaultChapter.id);
      fetchAllApplications(r.data);
    }).catch(() => toast.error('Failed to load chapters.'));
  }, [userChapterId, fetchAllApplications]);

  // Socket.IO subscription
  useEffect(() => {
    const socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    socketRef.current = socket;
    socket.on('gradPledgeUpdate', () => fetchAllApplications());
    return () => {
      socket.off('gradPledgeUpdate');
      socket.disconnect();
    };
  }, [fetchAllApplications]);

  // Derive per-tab view client-side — no re-fetch on tab switch
  const applications = activeChapterId === ALL_CHAPTERS_ID
    ? allApplications
    : allApplications.filter(a => a.chapter_id === activeChapterId);

  // Column sort toggle handler
  const handleSortToggle = (col: SortColumn) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortColumn(null);
      setSortDir('asc');
    }
  };

  // Sorting: column sort when active, otherwise incomplete-first then A→Z
  const sorted = (() => {
    const apps = [...applications];
    if (sortColumn) {
      return apps.sort((a, b) => {
        const aKey = getSortKey(a, sortColumn);
        const bKey = getSortKey(b, sortColumn);
        if (!aKey && !bKey) return 0;
        if (!aKey) return sortDir === 'asc' ? 1 : -1;
        if (!bKey) return sortDir === 'asc' ? -1 : 1;
        const cmp = aKey.localeCompare(bKey);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return apps.sort((a, b) => {
      const aComplete = isFullySigned(a);
      const bComplete = isFullySigned(b);
      if (aComplete !== bComplete) return aComplete ? 1 : -1;
      return (a.User?.name || '').localeCompare(b.User?.name || '');
    });
  })();

  // Filtering
  const filtered = sorted.filter(app => {
    if (filter === 'complete' && !isFullySigned(app)) return false;
    if (filter === 'incomplete' && isFullySigned(app)) return false;
    if (nameSearch) {
      const q = nameSearch.toLowerCase();
      const mentee = app.User?.name?.toLowerCase() || '';
      const mentor = (app.assignedMentor?.name || getPledgeFor(app, 'mentor')?.User?.name || '').toLowerCase();
      const committee = (app.assignedCommittee?.name || getPledgeFor(app, 'committee')?.User?.name || '').toLowerCase();
      const manager = (app.manager || '').toLowerCase().replace(/_/g, ' ').replace(/@.*$/, '');
      if (!mentee.includes(q) && !mentor.includes(q) && !committee.includes(q) && !manager.includes(q)) return false;
    }
    return true;
  });

  const grouped = { all: filtered };

  const progress = computeProgress(applications);
  const completedPct = progress.totalPossible > 0 ? Math.round((progress.totalSigned / progress.totalPossible) * 100) : 0;

  const isAllView = activeChapterId === ALL_CHAPTERS_ID;
  const canModify = !isAllView && (isAdmin || activeChapterId === userChapterId);
  const readOnlyTooltip = 'Read-only: contact your regional Admin to modify';

  // Selection helpers
  const incompleteIds = new Set(filtered.filter(a => hasReminderTarget(a)).map(a => a.id));
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const allSelected = incompleteIds.size > 0 && [...incompleteIds].every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(incompleteIds));
  };

  // Override (mark as signed)
  const handleOverrideConfirm = async () => {
    const { application, role } = overrideModal!;
    // Pass the role holder's user ID so the server can link override events to their timeline.
    const assignedUserId =
      role === 'mentor' ? (application.assignedMentor?.id ?? null) :
      role === 'committee' ? (application.assignedCommittee?.id ?? null) :
      role === 'mentee' ? (application.User?.id ?? null) :
      role === 'manager' ? (application.assignedManagerUser?.id ?? null) :
      null;
    try {
      const r = await axios.post('/api/grad_pledges/override', {
        application_id: application.id,
        signer_role: role,
        note: overrideNote || undefined,
        ...(assignedUserId != null ? { assigned_user_id: assignedUserId } : {}),
      });
      const newPledge = r.data;
      setAllApplications(prev => prev.map(app =>
        app.id === application.id
          ? { ...app, GradPledges: [...(app.GradPledges || []), newPledge] }
          : app
      ));
      // Bust timeline cache for this application so next open re-fetches fresh history
      setRowTimelines(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (k.startsWith(`${application.id}-`)) delete next[k]; });
        return next;
      });
      toast.success(`${SIGNER_ROLE_LABELS[role]} pledge marked as signed for ${application.User?.name}.`);
      setOverrideModal(null);
      setOverrideNote('');
    } catch (err: unknown) {
      if ((err as any).response?.status === 409) {
        toast.error('A pledge record already exists for this role — no change made.');
      } else {
        toast.error('Failed to mark pledge as signed. Please try again.');
      }
      setOverrideModal(null);
      setOverrideNote('');
    }
  };

  // Revoke
  const handleRevokeConfirm = async () => {
    const { pledge, menteeName } = revokeModal!;
    try {
      await axios.delete(`/api/grad_pledges/${pledge.id}`);
      setAllApplications(prev => prev.map(app =>
        app.id === pledge.application_id
          ? { ...app, GradPledges: (app.GradPledges || []).filter((p: any) => p.id !== pledge.id) }
          : app
      ));
      // Bust timeline cache for this application so next open re-fetches fresh history
      setRowTimelines(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (k.startsWith(`${pledge.application_id}-`)) delete next[k]; });
        return next;
      });
      toast.success(`Pledge revoked for ${menteeName}.`);
      setRevokeModal(null);
    } catch (err: unknown) {
      if ((err as any).response?.status === 404) {
        toast.error('Pledge record not found — it may have already been removed.');
      } else {
        toast.error('Failed to revoke pledge. Please try again.');
      }
      setRevokeModal(null);
    }
  };

  // Send reminder (per-row or bulk)
  const handleSendReminder = async (applicationIds: number[]) => {
    try {
      await Promise.all(
        applicationIds.map(id =>
          axios.post('/api/email_reminders/send_grad_pledge_reminder', { application_id: id })
        )
      );
      toast.success(`Reminder${applicationIds.length > 1 ? 's' : ''} sent successfully.`);
      setReminderModal(null);
      setReminderShowDetails(false);
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to send reminder(s). Please try again.');
      setReminderModal(null);
      setReminderShowDetails(false);
    }
  };

  const computeReminderRecipients = (applicationIds: number[]) => {
    const recipients: { menteeName: string; role: string; personName: string }[] = [];
    for (const id of applicationIds) {
      const app = applications.find(a => a.id === id);
      if (!app) continue;
      const menteeName = app.User?.name || '—';
      for (const role of SIGNER_ROLES) {
        if (!getPledgeFor(app, role)) {
          let personName: string | null;
          if (role === 'mentee') personName = menteeName;
          else if (role === 'mentor') personName = app.assignedMentor?.name || null;
          else if (role === 'committee') personName = app.assignedCommittee?.name || null;
          else personName = app.assignedManagerUser?.name || (app.manager ? app.manager.replace(/@.*$/, '').replace(/_/g, ' ') : null);
          if (!personName) continue; // skip vacancies
          recipients.push({ menteeName, role: SIGNER_ROLE_LABELS[role], personName });
        }
      }
    }
    return recipients;
  };

  // ── Row expand / drawer helpers ───────────────────────────────────────────────
  const toggleRowExpand = (appId: number) => {
    setExpandedRowId(prev => {
      if (prev === appId) {
        setExpandedTileKey(null);
        return null;
      }
      setExpandedTileKey(null);
      return appId;
    });
  };

  const fetchTileTimeline = (appId: number, userId: number) => {
    const key = `${appId}-${userId}`;
    if (rowTimelines[key] !== undefined && rowTimelines[key] !== 'error') return;
    setRowTimelines(prev => ({ ...prev, [key]: 'loading' }));
    axios.get('/api/grad_pledges/user-timeline', { params: { application_id: appId, user_id: userId } })
      .then(r => setRowTimelines(prev => ({ ...prev, [key]: r.data })))
      .catch(() => setRowTimelines(prev => ({ ...prev, [key]: 'error' })));
  };

  const toggleTileKey = (appId: number, userId: number) => {
    const key = `${appId}-${userId}`;
    setExpandedTileKey(prev => prev === key ? null : key);
    fetchTileTimeline(appId, userId);
  };

  const renderTimeline = (key: string) => {
    const data = rowTimelines[key];
    if (!data || data === 'loading') return <div className="pledge-timeline-loading">Loading history…</div>;
    if (data === 'error') return <div className="pledge-timeline-empty">Could not load history.</div>;
    if ((data as any[]).length === 0) return <div className="pledge-timeline-empty">No recorded history yet.</div>;
    return (
      <ol className="pledge-timeline">
        {[...(data as any[])].reverse().map((event, i) => (
          <li key={i} className={`pledge-timeline-event pledge-timeline-event--${event.type}`}>
            <span className="pledge-timeline-dot" />
            <div className="pledge-timeline-body">
              <span className="pledge-timeline-label">
                {event.type === 'pledged' ? 'Pledged support' :
                 event.type === 'withdrew' ? 'Withdrew support' :
                 event.type === 'override' ? `Admin override${event.note ? ` — ${event.note}` : ''}` :
                 event.type}
              </span>
              <span className="pledge-timeline-ts">
                {new Date(event.timestamp).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
              </span>
            </div>
          </li>
        ))}
      </ol>
    );
  };

  const renderDrawer = (app: any) => {
    const allRoles = ['mentee', 'mentor', 'committee', 'manager'] as const;
    return (
      <tr className="grad-pledges-drawer-row">
        <td colSpan={8} className="grad-pledges-drawer-cell">
          <div className="grad-pledges-drawer-content">
            <div className="grad-pledges-drawer-tiles">
              {allRoles.map(role => {
                const pledge = getPledgeFor(app, role);
                const signerUser = pledge?.User || (role === 'mentee' ? app.User : null);
                const assignedUser =
                  role === 'mentor' ? (app.assignedMentor || null) :
                  role === 'committee' ? (app.assignedCommittee || null) :
                  role === 'manager' && (app.assignedManagerUser || app.manager)
                    ? (app.assignedManagerUser || { name: app.manager.replace(/@.*$/, '').replace(/_/g, ' ') })
                    : role === 'mentee' ? app.User : null;
                const displayUser = signerUser || assignedUser;

                let userId: number | null = null;
                if (pledge) {
                  userId = pledge.signed_by ?? null;
                } else if (role === 'mentee') {
                  userId = app.User?.id ?? null;
                } else if (role === 'mentor') {
                  userId = app.assignedMentor?.id ?? null;
                } else if (role === 'committee') {
                  userId = app.assignedCommittee?.id ?? null;
                } else if (role === 'manager') {
                  userId = app.assignedManagerUser?.id ?? null;
                }

                const tileKey = userId != null ? `${app.id}-${userId}` : null;
                const isOpen = tileKey !== null && expandedTileKey === tileKey;
                const isSigned = !!pledge;

                return (
                  <React.Fragment key={role}>
                    <div
                      className={`grad-pledges-drawer-tile${isSigned ? ' drawer-tile--signed' : ' drawer-tile--pending'}${userId != null ? ' drawer-tile--clickable' : ''}`}
                      onClick={userId != null ? () => toggleTileKey(app.id, userId!) : undefined}
                      title={userId != null ? 'Click to view pledge history' : undefined}
                      role={userId != null ? 'button' : undefined}
                      tabIndex={userId != null ? 0 : undefined}
                      onKeyDown={userId != null ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleTileKey(app.id, userId!); } } : undefined}
                    >
                      <UserAvatar user={displayUser} size={32} />
                      <div className="grad-pledges-drawer-tile-info">
                        <span className="grad-pledges-drawer-tile-name">
                          {stripNameTitle(displayUser?.name) || 'Unassigned'}
                        </span>
                        <span className="grad-pledges-drawer-tile-role">{SIGNER_ROLE_LABELS[role]}</span>
                      </div>
                      {isSigned ? (
                        <span className="grad-pledges-drawer-tile-date">
                          {pledge.created_at ? new Date(pledge.created_at).toLocaleDateString() : ''}
                        </span>
                      ) : (
                        <span className="grad-pledges-drawer-tile-awaiting">Awaiting signature</span>
                      )}
                      {isSigned
                        ? <FontAwesomeIcon icon={faCheck} className="grad-pledges-drawer-tile-check" />
                        : <FontAwesomeIcon icon={faClock} className="grad-pledges-drawer-tile-clock" />
                      }
                      {userId != null && (
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="grad-pledges-drawer-tile-chevron" />
                      )}
                    </div>
                    {isOpen && tileKey && (
                      <div className="grad-pledges-drawer-timeline-panel">
                        {renderTimeline(tileKey)}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderRow = (app: any) => {
    const canRemind = hasReminderTarget(app);
    const isSelected = selectedIds.has(app.id);
    const isExpanded = expandedRowId === app.id;
    return (
      <React.Fragment key={app.id}>
        <tr className={isExpanded ? 'grad-pledges-row-expanded' : ''}>
          <td className="grad-pledges-checkbox-col">
            {canRemind && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(app.id)}
                aria-label={`Select ${app.User?.name}`}
              />
            )}
          </td>
          <td className={getPledgeFor(app, 'mentee') ? 'pledge-cell-signed' : 'pledge-cell-unsigned'}>
            <div className="grad-pledges-mentee-cell">
              <UserAvatar user={app.User} size={28} />
              <span>{stripNameTitle(app.User?.name) || '—'}</span>
            </div>
          </td>
          {PLEDGE_ROLES.map(role => {
            const pledge = getPledgeFor(app, role);
            const assignedUser =
              role === 'mentor' ? (app.assignedMentor || null) :
              role === 'committee' ? (app.assignedCommittee || null) :
              role === 'manager' && (app.assignedManagerUser || app.manager)
                ? (app.assignedManagerUser || { name: app.manager.replace(/@.*$/, '').replace(/_/g, ' ') })
                : null;
            return (
              <td key={role} className={pledge ? 'pledge-cell-signed' : 'pledge-cell-unsigned'}>
                <PledgeCell pledge={pledge} assignedUser={assignedUser} />
              </td>
            );
          })}
          <td><OverallChip application={app} /></td>
          <td>
            <div className="grad-pledges-actions">
              {SIGNER_ROLES.map(role => {
                const pledge = getPledgeFor(app, role);
                return pledge ? (
                  <PrismButton
                    key={`rev-${role}`}
                    display="outline"
                    size="xxs"
                    disabled={!canModify}
                    title={canModify ? `Revoke ${SIGNER_ROLE_LABELS[role]} pledge` : readOnlyTooltip}
                    onPrismClick={() => setRevokeModal({ pledge, menteeName: app.User?.name })}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </PrismButton>
                ) : (
                  <PrismButton
                    key={role}
                    display="outline"
                    size="xxs"
                    disabled={!canModify}
                    title={canModify ? `Mark ${SIGNER_ROLE_LABELS[role]} as signed` : readOnlyTooltip}
                    onPrismClick={() => { setOverrideModal({ application: app, role }); setOverrideNote(''); }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </PrismButton>
                );
              })}
              {canRemind && (
                <PrismButton
                  display="outline"
                  size="xxs"
                  title="Send reminder to unsigned parties"
                  onPrismClick={() => setReminderModal({ applicationIds: [app.id] })}
                >
                  <FontAwesomeIcon icon={faBell} />
                </PrismButton>
              )}
            </div>
          </td>
          <td className="grad-pledges-expand-col">
            <button
              className="grad-pledges-expand-btn"
              onClick={() => toggleRowExpand(app.id)}
              title={isExpanded ? 'Collapse pledge history' : 'Show pledge history'}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} pledge history for ${app.User?.name}`}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
            </button>
          </td>
        </tr>
        {isExpanded && renderDrawer(app)}
      </React.Fragment>
    );
  };

  return (
    <AdminPane as="div" width="full" className="grad-pledges-container">
      <div className="grad-pledges-header">
        <PrismBanner tone="info" layout="inline">
          <strong>Graduation Pledges:</strong>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: 0, marginTop: '0.5rem', listStyleType: 'disc' }}>
            <li>Tracks pledge acknowledgements for all accepted mentees — Mentee, Mentor, Manager, and Committee must each sign</li>
            <li><strong>Mark as Signed</strong> ✓ overrides and manually records a signature on behalf of the named party</li>
            <li><strong>Revoke</strong> 🗑 removes a pledge record; both actions are permanently audit-logged</li>
            <li>Officers can only modify their own chapter's records; Admins can modify all chapters</li>
            <li>Use <strong>Send Reminder</strong> 🔔 to email unsigned parties for a specific mentee, or select multiple rows for a bulk send</li>
          </ul>
        </PrismBanner>
      </div>

      {/* Section tabs: Pledge Status | Pledge Reminders */}
      <div className="grad-pledges-section-tabs">
        <button
          className={`grad-pledges-section-tab${sectionTab === 'status' ? ' active' : ''}`}
          onClick={() => setSectionTab('status')}
        >
          Pledge Status
        </button>
        {activeChapterId !== ALL_CHAPTERS_ID && (
          <button
            className={`grad-pledges-section-tab${sectionTab === 'reminders' ? ' active' : ''}`}
            onClick={() => setSectionTab('reminders')}
          >
            Pledge Reminders
          </button>
        )}
      </div>

      {/* Chapter tabs */}
      <div className="grad-pledges-tabs">
        <button
          className={`grad-pledges-tab${activeChapterId === ALL_CHAPTERS_ID ? ' active' : ''}`}
          onClick={() => { setActiveChapterId(ALL_CHAPTERS_ID); setSelectedIds(new Set()); setSortColumn(null); setSortDir('asc'); }}
        >
          All
        </button>
        {chapters.map(chapter => (
          <button
            key={chapter.id}
            className={`grad-pledges-tab${activeChapterId === chapter.id ? ' active' : ''}`}
            onClick={() => { setActiveChapterId(chapter.id); setSelectedIds(new Set()); setSortColumn(null); setSortDir('asc'); }}
          >
            {chapter.location}
          </button>
        ))}
      </div>

      {sectionTab === 'status' ? (
        <>
          {loading ? (
            <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
              <PrismLoading as="spinner" container="inline" />
            </div>
          ) : (
        <>
          {/* Progress summary */}
          <div className="grad-pledges-progress">
            <div className="grad-pledges-progress-bar-wrap">
              <div style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                <strong>{progress.totalSigned}</strong> / {progress.totalPossible} signatures ({completedPct}%)
              </div>
              <div className="grad-pledges-progress-bar">
                <div className="grad-pledges-progress-bar-fill" style={{ width: `${completedPct}%` }} />
              </div>
            </div>
            <div className="grad-pledges-role-counters">
              {SIGNER_ROLES.map(role => (
                <span key={role} className="grad-pledges-role-counter">
                  <strong>{SIGNER_ROLE_LABELS[role]}:</strong>{' '}
                  {progress.roleCounts[role].signed}/{progress.total}
                </span>
              ))}
            </div>
          </div>

          {/* Filter bar */}
          <div className="grad-pledges-filters">
            {['all', 'complete', 'incomplete'].map(f => (
              <button
                key={f}
                className={`grad-pledges-filter-btn${filter === f ? ' active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'complete' ? 'Fully Signed' : 'Incomplete'}
              </button>
            ))}
            <input
              className="grad-pledges-search form-control form-control-sm"
              placeholder="Search by name…"
              value={nameSearch}
              onChange={e => setNameSearch(e.target.value)}
            />
            {nameSearch && (
              <button
                className="grad-pledges-search-clear"
                onClick={() => setNameSearch('')}
                title="Clear search"
              >
                <img src={clearLogoSrc} alt="Clear" />
              </button>
            )}
            <span className="grad-pledges-count">Showing {filtered.length} of {progress.total} Triads</span>

          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="grad-pledges-bulk-bar">
              <span>{selectedIds.size} selected</span>
              <PrismButton
                display="outline"
                size="xxs"
                onPrismClick={() => setReminderModal({ applicationIds: [...selectedIds] as number[] })}
              >
                <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.25rem' }} />
                Send Reminders to Selected
              </PrismButton>
              <PrismButton
                display="outline"
                size="xxs"
                onPrismClick={() => setSelectedIds(new Set())}
              >
                Clear Selection
              </PrismButton>
            </div>
          )}

          {/* Table */}
          <div className="grad-pledges-table-wrap">
            <table className="grad-pledges-table">
              <thead>
                <tr>
                  <th className="grad-pledges-checkbox-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      title="Select all incomplete"
                      disabled={incompleteIds.size === 0}
                    />
                  </th>
                  <th>
                    <button
                      className={`grad-pledges-sort-btn${sortColumn === 'mentee' ? ' active' : ''}`}
                      onClick={() => handleSortToggle('mentee')}
                    >
                      Mentee
                      <FontAwesomeIcon
                        icon={sortColumn === 'mentee' ? (sortDir === 'asc' ? faChevronUp : faChevronDown) : faSort}
                        className="grad-pledges-sort-icon"
                      />
                    </button>
                  </th>
                  <th>
                    <button
                      className={`grad-pledges-sort-btn${sortColumn === 'mentor' ? ' active' : ''}`}
                      onClick={() => handleSortToggle('mentor')}
                    >
                      {SIGNER_ROLE_LABELS['mentor']}
                      <FontAwesomeIcon
                        icon={sortColumn === 'mentor' ? (sortDir === 'asc' ? faChevronUp : faChevronDown) : faSort}
                        className="grad-pledges-sort-icon"
                      />
                    </button>
                  </th>
                  <th>
                    <button
                      className={`grad-pledges-sort-btn${sortColumn === 'committee' ? ' active' : ''}`}
                      onClick={() => handleSortToggle('committee')}
                    >
                      {SIGNER_ROLE_LABELS['committee']}
                      <FontAwesomeIcon
                        icon={sortColumn === 'committee' ? (sortDir === 'asc' ? faChevronUp : faChevronDown) : faSort}
                        className="grad-pledges-sort-icon"
                      />
                    </button>
                  </th>
                  <th>
                    <button
                      className={`grad-pledges-sort-btn${sortColumn === 'manager' ? ' active' : ''}`}
                      onClick={() => handleSortToggle('manager')}
                    >
                      {SIGNER_ROLE_LABELS['manager']}
                      <FontAwesomeIcon
                        icon={sortColumn === 'manager' ? (sortDir === 'asc' ? faChevronUp : faChevronDown) : faSort}
                        className="grad-pledges-sort-icon"
                      />
                    </button>
                  </th>
                  <th>Overall</th>
                  <th>Actions</th>
                  <th className="grad-pledges-expand-col" />
                </tr>
              </thead>
              <tbody>
                {grouped.all.map(renderRow)}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--color-text-faded)', padding: '1.5rem' }}>
                      No mentees match the current filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Override modal */}
      {overrideModal && (
        <ConfirmModal
          title={`Mark ${SIGNER_ROLE_LABELS[overrideModal.role]} as Signed`}
          onConfirm={handleOverrideConfirm}
          onCancel={() => { setOverrideModal(null); setOverrideNote(''); }}
          confirmLabel="Mark as Signed"
          confirmDisabled={!overrideNote.trim()}
        >
          <p>
            You are manually recording a <strong>{SIGNER_ROLE_LABELS[overrideModal.role]}</strong> pledge
            acknowledgement for <strong>{overrideModal.application.User?.name}</strong>.
            This action is permanent and will be audit-logged.
          </p>
          <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
            Note <span style={{ color: 'var(--prism-color-danger, #c0392b)' }}>*</span>
          </label>
          <input
            className="form-control form-control-sm"
            placeholder="Required — reason for manual override…"
            value={overrideNote}
            onChange={e => setOverrideNote(e.target.value)}
          />
        </ConfirmModal>
      )}

      {/* Revoke modal */}
      {revokeModal && (
        <ConfirmModal
          title="Revoke Pledge"
          onConfirm={handleRevokeConfirm}
          onCancel={() => setRevokeModal(null)}
          confirmLabel="Revoke"
          confirmVariant="danger"
        >
          <p>
            Revoke the <strong>{SIGNER_ROLE_LABELS[revokeModal.pledge.signer_role]}</strong> pledge
            for <strong>{revokeModal.menteeName}</strong>?
            This is permanent and will be audit-logged.
          </p>
        </ConfirmModal>
      )}

      {/* Reminder modal */}
      {reminderModal && (() => {
        const recipients = computeReminderRecipients(reminderModal.applicationIds);
        const triadCount = reminderModal.applicationIds.length;
        return (
          <ConfirmModal
            title="Send Reminder"
            onConfirm={() => handleSendReminder(reminderModal.applicationIds)}
            onCancel={() => { setReminderModal(null); setReminderShowDetails(false); }}
            confirmLabel="Send Reminder"
          >
            <p style={{ marginBottom: '0.5rem' }}>
              Send a reminder email to{' '}
              <strong>{recipients.length} {recipients.length === 1 ? 'person' : 'people'}</strong>
              {' '}across {triadCount === 1 ? 'this triad' : `${triadCount} selected triads`}?{' '}
              <button
                type="button"
                onClick={() => setReminderShowDetails(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '0.875rem', color: 'var(--prism-color-primary, #0057b8)', textDecoration: 'underline' }}
              >
                {reminderShowDetails ? 'Hide' : 'Show'} people
              </button>
            </p>
            {reminderShowDetails && (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--prism-color-border, #dee2e6)', borderRadius: 4, padding: '0.5rem', fontSize: '0.8125rem' }}>
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  {recipients.map((r, i) => (
                    <li key={i}>
                      <strong>{r.personName}</strong> — {r.role}
                      {triadCount > 1 && <span style={{ color: 'var(--prism-color-text-secondary, #6c757d)' }}> (for {r.menteeName})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </ConfirmModal>
        );
      })()}
        </>
      ) : (
        <PledgeRemindersTab currentUser={currentUser} activeChapterId={activeChapterId} />
      )}
    </AdminPane>
  );
};

export default GraduationPledges;
